import { useState, useEffect } from 'react';
import { DollarSign, ShoppingCart, Package, AlertTriangle, TrendingUp, Clock } from 'lucide-react';
import AdminLayout from '../../components/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Skeleton } from '../../components/ui/skeleton';
import api, { formatCurrency } from '../../lib/api';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line } from 'recharts';

const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [salesData, setSalesData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [statsRes, salesRes] = await Promise.all([
          api.get('/admin/dashboard'),
          api.get('/admin/reports/sales?period=daily'),
        ]);
        setStats(statsRes.data);
        setSalesData(salesRes.data.data || []);
      } catch (error) {
        console.error('Failed to load dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  // Seed data if needed
  useEffect(() => {
    const seedData = async () => {
      try {
        await api.post('/admin/seed');
      } catch (error) {
        // Ignore
      }
    };
    seedData();
  }, []);

  if (loading) {
    return (
      <AdminLayout title="Dashboard">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {[...Array(4)].map((_, i) => (
            <Skeleton key={i} className="h-32 rounded-2xl" />
          ))}
        </div>
        <Skeleton className="h-80 rounded-2xl" />
      </AdminLayout>
    );
  }

  const statCards = [
    {
      title: 'Vendas Totais',
      value: formatCurrency(stats?.total_sales || 0),
      icon: DollarSign,
      color: 'bg-green-500',
      subtext: `${stats?.total_orders || 0} pedidos`,
    },
    {
      title: 'Vendas Hoje',
      value: formatCurrency(stats?.today_sales || 0),
      icon: TrendingUp,
      color: 'bg-blue-500',
      subtext: 'Atualizado agora',
    },
    {
      title: 'Vendas Mês',
      value: formatCurrency(stats?.monthly_sales || 0),
      icon: BarChart,
      color: 'bg-purple-500',
      subtext: 'Mês atual',
    },
    {
      title: 'Pedidos Pendentes',
      value: stats?.pending_orders || 0,
      icon: Clock,
      color: 'bg-yellow-500',
      subtext: 'Aguardando',
    },
  ];

  return (
    <AdminLayout title="Dashboard">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {statCards.map((card, index) => (
          <Card key={index} className="rounded-2xl" data-testid={`stat-card-${index}`}>
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm text-muted-foreground mb-1">{card.title}</p>
                  <p className="text-2xl font-bold font-heading">{card.value}</p>
                  <p className="text-xs text-muted-foreground mt-1">{card.subtext}</p>
                </div>
                <div className={`p-3 rounded-xl ${card.color}`}>
                  <card.icon className="w-5 h-5 text-white" />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Alerts */}
      {(stats?.low_stock_count > 0 || stats?.pending_orders > 0) && (
        <div className="grid md:grid-cols-2 gap-4 mb-8">
          {stats?.low_stock_count > 0 && (
            <Card className="rounded-2xl border-warning/50 bg-warning/5">
              <CardContent className="p-4 flex items-center gap-4">
                <div className="p-3 rounded-xl bg-warning">
                  <AlertTriangle className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="font-medium">Estoque Baixo</p>
                  <p className="text-sm text-muted-foreground">
                    {stats.low_stock_count} produto(s) com estoque abaixo do mínimo
                  </p>
                </div>
              </CardContent>
            </Card>
          )}
          
          {stats?.pending_orders > 0 && (
            <Card className="rounded-2xl border-primary/50 bg-primary/5">
              <CardContent className="p-4 flex items-center gap-4">
                <div className="p-3 rounded-xl bg-primary">
                  <ShoppingCart className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="font-medium">Pedidos Pendentes</p>
                  <p className="text-sm text-muted-foreground">
                    {stats.pending_orders} pedido(s) aguardando processamento
                  </p>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Sales Chart */}
      <div className="grid lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-2 rounded-2xl">
          <CardHeader>
            <CardTitle className="font-heading">Vendas dos Últimos 30 Dias</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-80" data-testid="sales-chart">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={salesData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis 
                    dataKey="_id" 
                    tick={{ fontSize: 12 }}
                    tickFormatter={(value) => value?.slice(-5) || ''}
                  />
                  <YAxis 
                    tick={{ fontSize: 12 }}
                    tickFormatter={(value) => `R$${value}`}
                  />
                  <Tooltip 
                    formatter={(value) => [formatCurrency(value), 'Vendas']}
                    labelFormatter={(label) => `Data: ${label}`}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="total" 
                    stroke="hsl(221, 83%, 53%)" 
                    strokeWidth={2}
                    dot={{ fill: 'hsl(221, 83%, 53%)' }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Top Products */}
        <Card className="rounded-2xl">
          <CardHeader>
            <CardTitle className="font-heading">Produtos Mais Vendidos</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4" data-testid="top-products">
              {stats?.top_products?.length > 0 ? (
                stats.top_products.map((product, index) => (
                  <div key={product._id || index} className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-sm ${
                      index === 0 ? 'bg-yellow-500' : index === 1 ? 'bg-slate-400' : index === 2 ? 'bg-amber-600' : 'bg-slate-300'
                    }`}>
                      {index + 1}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm truncate">{product.name || 'Produto'}</p>
                      <p className="text-xs text-muted-foreground">{product.total_sold} vendidos</p>
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-muted-foreground text-sm text-center py-4">
                  Nenhuma venda registrada ainda
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default AdminDashboard;
